import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { KeyRound, ArrowLeft, Mail, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import api from '../services/api';

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.forgotPassword(email);
      setSent(true);
    } catch (err) {
      setError(err.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50 dark:bg-slate-950" data-testid="forgot-password-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        <button onClick={() => navigate('/login')} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Sign In
        </button>

        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-soft-lg p-7 border border-slate-200 dark:border-slate-800">
          {sent ? (
            <div className="text-center" data-testid="forgot-sent">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-500 flex items-center justify-center mb-3">
                <Mail className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-xl font-heading font-bold mb-2">Check your email</h2>
              <p className="text-sm text-muted-foreground mb-5">
                If an account exists for <strong>{email}</strong>, you'll receive a reset link shortly. The link expires in 15 minutes.
              </p>
              <Button onClick={() => navigate('/login')} className="w-full">Back to Sign In</Button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-xl bg-brand-deep-blue flex items-center justify-center">
                  <KeyRound className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-heading font-bold">Reset your password</h2>
                  <p className="text-xs text-muted-foreground">Individual teachers only. School accounts reset via their admin.</p>
                </div>
              </div>
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="fpw-email">Email</Label>
                  <Input id="fpw-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" required />
                </div>
                <Button type="submit" className="w-full h-11 bg-brand-deep-blue hover:bg-brand-deep-blue/90" disabled={loading} data-testid="forgot-submit">
                  {loading ? 'Sending…' : 'Send Reset Link'}
                </Button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const token = new URLSearchParams(window.location.search).get('token') || '';

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError("Passwords don't match"); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (!token) { setError('Missing reset token'); return; }
    setLoading(true);
    try {
      await api.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err.message || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50 dark:bg-slate-950" data-testid="reset-password-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full">
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-soft-lg p-7 border border-slate-200 dark:border-slate-800">
          {done ? (
            <div className="text-center" data-testid="reset-done">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-500 flex items-center justify-center mb-3">
                <Check className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-xl font-heading font-bold mb-2">Password updated</h2>
              <p className="text-sm text-muted-foreground mb-5">You've been signed out everywhere. Sign in again with your new password.</p>
              <Button onClick={() => navigate('/login')} className="w-full">Go to Sign In</Button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-xl bg-brand-deep-blue flex items-center justify-center">
                  <KeyRound className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-heading font-bold">Set a new password</h2>
                  <p className="text-xs text-muted-foreground">Minimum 8 chars with letters + digits</p>
                </div>
              </div>
              {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}
              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="new-pwd">New Password</Label>
                  <Input id="new-pwd" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" required />
                </div>
                <div>
                  <Label htmlFor="confirm-pwd">Confirm Password</Label>
                  <Input id="confirm-pwd" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" required />
                </div>
                <Button type="submit" className="w-full h-11 bg-brand-deep-blue hover:bg-brand-deep-blue/90" disabled={loading} data-testid="reset-submit">
                  {loading ? 'Updating…' : 'Update Password'}
                </Button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
